"use client"

import type React from "react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { Eye, EyeOff, Check, X } from "lucide-react"
import { env } from "../config/env"
import { generateKeyPairFromPassword } from "../crypto/crypto"
import { toast } from "sonner"

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate()
  const [username, setUsername] = useState("")
  const [name, setName] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
  })

  const validatePassword = (pwd: string) => {
    setPasswordRequirements({
      minLength: pwd.length >= 8,
      hasUppercase: /[A-Z]/.test(pwd),
      hasLowercase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecial: /[_!@#$%^&*(),.?":{}|<>]/.test(pwd),
    })
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value
    setPassword(newPassword)
    validatePassword(newPassword)
  }

  const isPasswordValid = Object.values(passwordRequirements).every((req) => req)

  const handleRegister = async (e: React.FormEvent) => {
    const API_URL = env.API_URL
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      if (!isPasswordValid) {
        setError("Password does not meet all requirements")
        setLoading(false)
        return
      }

      if (password !== confirmPassword) {
        setError("Passwords do not match")
        setLoading(false)
        return
      }

      // Generate key pair from password
      const keyPair = await generateKeyPairFromPassword(password, username)

      // Send public key to server along with credentials
      await axios.post(`${API_URL}/auth/register`, {
        username,
        name,
        password,
        publicKeyX: keyPair.publicKeyX,
        publicKeyY: keyPair.publicKeyY,
      })

      toast.success("Registration successful!", {
        description: ` You can now login to your account`,
        action: {
          label: "Login",
          onClick: () => (location.href = "/login"),
        },
      })
      navigate("/login")
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      const errorMessage = error.response?.data?.error || "Login failed"
      setError(errorMessage || "Registration failed")
      toast.error("Failed to register a new account", {
        description: `Error creating a new account because of: ${errorMessage}`,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-slate-900 to-slate-800 px-4 py-8">
      <div className="w-full max-w-md bg-slate-800 rounded-lg shadow-xl p-6 sm:p-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 text-center">MeeChat</h1>
        <p className="text-sm sm:text-base text-slate-400 text-center mb-6 sm:mb-8">Create your secure account</p>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded mb-4">{error}</div>
        )}

        <form onSubmit={handleRegister} className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="username"
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                placeholder="••••••••"
                className="w-full px-3 sm:px-4 py-2 pr-10 text-sm sm:text-base bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {password && (
              <div className="mt-2 space-y-1 text-xs">
                <div
                  className={`flex items-center gap-1 ${passwordRequirements.minLength ? "text-green-400" : "text-slate-400"}`}
                >
                  {passwordRequirements.minLength ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  <span>At least 8 characters</span>
                </div>
                <div
                  className={`flex items-center gap-1 ${passwordRequirements.hasUppercase ? "text-green-400" : "text-slate-400"}`}
                >
                  {passwordRequirements.hasUppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  <span>One uppercase letter</span>
                </div>
                <div
                  className={`flex items-center gap-1 ${passwordRequirements.hasLowercase ? "text-green-400" : "text-slate-400"}`}
                >
                  {passwordRequirements.hasLowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  <span>One lowercase letter</span>
                </div>
                <div
                  className={`flex items-center gap-1 ${passwordRequirements.hasNumber ? "text-green-400" : "text-slate-400"}`}
                >
                  {passwordRequirements.hasNumber ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  <span>One number</span>
                </div>
                <div
                  className={`flex items-center gap-1 ${passwordRequirements.hasSpecial ? "text-green-400" : "text-slate-400"}`}
                >
                  {passwordRequirements.hasSpecial ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  <span>One special character</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 sm:px-4 py-2 pr-10 text-sm sm:text-base bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <div className="mt-1 flex items-center gap-1 text-xs text-red-400">
                <X className="w-3 h-3" />
                <span>Passwords do not match</span>
              </div>
            )}
            {confirmPassword && password === confirmPassword && (
              <div className="mt-1 flex items-center gap-1 text-xs text-green-400">
                <Check className="w-3 h-3" />
                <span>Passwords match</span>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white font-semibold py-2.5 sm:py-2 text-sm sm:text-base rounded transition cursor-pointer"
          >
            {loading ? "Registering..." : "Register"}
          </button>
        </form>

        <p className="text-slate-400 text-center mt-4 sm:mt-6 text-sm sm:text-base">
          Already have an account?{" "}
          <a href="/login" className="text-blue-400 hover:text-blue-300">
            Login
          </a>
        </p>
      </div>
    </div>
  )
}
